import { useMemo } from 'react';
import useAutoComplete, { Item } from '@v2/shared/lib/useAutoComplete';
import Currency from '@v2/shared/components/Currency';
import { useSite } from '@v2/shared/lib/site';
import { SaleData } from './SaleProvider';
import { PRODUCT_TYPE_MAGAZINE, PRODUCT_TYPE_POSTCARD, SaleProduct } from './types';
import { calculatePrice, findDefaultTerm, issuesForTerm, pricePerIssue, termPercentage } from './pricing';
import { Restrictions, shouldHideProduct } from './restrictions';

const MAX_SUGGESTIONS = 10;

/**
 * One searchable entry. The url is never followed — SearchForm renders it as a
 * suggestion and we intercept the choice — but the autocomplete's Item carries one, so
 * the ids ride there instead.
 */
type SaleSearchEntry = Item & { categoryId: number; productId: number };

/**
 * The sale's own search index.
 *
 * The header's search box on this page is not the site search. It searches *this sale*,
 * it is built from the JSON the page already loaded, and it never leaves the browser —
 * which is the reason it can be an exact scope rather than an approximation of one, and
 * why it answers instantly.
 *
 * It is also the reason the restrictions have to be applied here as well as in the
 * grid. A product hidden from this visitor must not be reachable by typing its name,
 * or the search is a way around the restriction.
 */
export function useSaleSearchIndex(
  data: SaleData | null,
  restrictions: Restrictions
): SaleSearchEntry[] {
  return useMemo(() => {
    if (!data) {
      return [];
    }

    const entries: SaleSearchEntry[] = [];
    const seen = new Set<string>();

    data.categories.forEach((category) => {
      (data.productsByCategory[category.id] || []).forEach((product: SaleProduct) => {
        if (shouldHideProduct(product, restrictions)) {
          return;
        }

        // The same title can be in several categories. The suggestion list shows it
        // once — the first category wins, which is the one nearest the top of the page.
        const key = product.name.trim().toLowerCase();

        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        entries.push({
          title: product.name,
          image: product.images.normal,
          detail: <SuggestionPricing product={product} />,
          categoryId: category.id,
          productId: product.id,
        });
      });
    });

    return entries;
  }, [data, restrictions]);
}

/**
 * What a suggestion says about the product besides its name.
 *
 * The legacy dropdown showed the sale price, the price per issue and the struck-through
 * cover price, and that is what this shows — the same three figures, because choosing
 * between magazines is choosing between prices and a list of bare titles makes the
 * customer open each one to find out.
 *
 * Priced on the term the tile would open on, so the number in the dropdown is the number
 * on the tile the suggestion scrolls to. The legacy version got this wrong in a way worth
 * not repeating: its `findDefaultTerm` never returned a default (a `return` inside a
 * `forEach`), and it struck through the raw `msrp` without the term's percentage, so a
 * 2-year term advertised a 1-year cover price.
 *
 * Nothing is struck through on a postcard. It carries an `msrp` like anything else, but
 * its tile shows no comparison price (see SaleMsrp), so the dropdown was the one place on
 * the page claiming a $0.99 card was once $4.99.
 *
 * Its own small component rather than a string built in the loop, because money is
 * rendered by `Currency` — the site decides the symbol and the placement, not this file.
 */
function SuggestionPricing({ product }: { product: SaleProduct }) {
  const site = useSite();

  const term = findDefaultTerm(product);
  const price = calculatePrice(term, product);
  const perIssue = pricePerIssue(price, issuesForTerm(product, term));
  const scale = 1 + termPercentage(term) / 100;
  const msrp = Math.round((product.msrp * scale + Number.EPSILON) * 100) / 100;

  return (
    <span className="text-xs">
      <span className="font-semibold text-primary-600">
        <Currency value={price} currencyCode={site.currency} />
        {product.product_type === PRODUCT_TYPE_MAGAZINE && perIssue !== null && (
          <span className="font-normal">
            {' ('}
            <Currency value={perIssue} currencyCode={site.currency} />
            /issue)
          </span>
        )}
      </span>
      {product.product_type !== PRODUCT_TYPE_POSTCARD && msrp > price && (
        <span className="text-slate-500 ml-2 line-through">
          <Currency value={msrp} currencyCode={site.currency} />
        </span>
      )}
    </span>
  );
}

/**
 * Substring match, ranked so that a title *starting* with what was typed comes first.
 *
 * Deliberately not a fuzzy matcher. The corpus is one sale — a few hundred titles the
 * visitor is looking at — so the useful question is "which of these did I mean", and a
 * prefix beats a match in the middle of a word every time.
 */
function search(entries: SaleSearchEntry[], query: string): SaleSearchEntry[] {
  const needle = query.trim().toLowerCase();

  if (needle === '') {
    return [];
  }

  const startsWith: SaleSearchEntry[] = [];
  const contains: SaleSearchEntry[] = [];

  for (const entry of entries) {
    const title = entry.title.toLowerCase();
    const at = title.indexOf(needle);

    if (at === 0) {
      startsWith.push(entry);
    } else if (at > 0) {
      contains.push(entry);
    }

    if (startsWith.length >= MAX_SUGGESTIONS) {
      break;
    }
  }

  return [...startsWith, ...contains].slice(0, MAX_SUGGESTIONS);
}

export type SaleSearchResult = ReturnType<typeof useAutoComplete> & {
  /** True once a query has been typed that matched nothing in this sale. */
  noResults: boolean;
};

/**
 * The header's search behaviour on a sale page.
 *
 * Shaped to `SearchAutoCompleteProps` so that `shared/components/SearchForm` renders it
 * unchanged — the site search and this one look and keyboard-navigate identically, and
 * only the source of the suggestions differs.
 */
export default function useSaleSearch(
  entries: SaleSearchEntry[],
  onSelect: (categoryId: number, productId: number) => void
): SaleSearchResult {
  const autoComplete = useAutoComplete(
    async (query: string) => search(entries, query),
    (item: Item) => {
      const entry = entries.find(
        (candidate) => candidate.title.toLowerCase() === item.title.trim().toLowerCase()
      );

      if (entry) {
        onSelect(entry.categoryId, entry.productId);
      }
    }
  );

  return {
    ...autoComplete,
    noResults: autoComplete.open && autoComplete.query.trim() !== '' && autoComplete.items.length === 0,
  };
}
